// src/Dashboard/pages/teacher/Attendance/components/AttendanceGroupPickerPanel.jsx

import React, { useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaSearch,
  FaUsers,
} from "react-icons/fa";

function getGroupColorClass(group) {
  const color = String(group?.color_groupe || "").trim();

  if (color.startsWith("bg-")) {
    return `${color} text-slate-900 ring-1 ring-black/5 dark:ring-white/10`;
  }

  return "bg-white text-slate-800 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700";
}

function getDojoName(group) {
  return (
    group?.dojo_nom ||
    group?.Dojo?.dojo_nom ||
    group?.dojo?.dojo_nom ||
    ""
  );
}

export default function AttendanceGroupPickerPanel({
  loading,
  filters,
  setFilters,
  seasons = [],
  dojos = [],
  groups = [],
}) {
  const [search, setSearch] = useState("");

  const hasSeveralDojos = dojos.length > 1;

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();

    return (groups || [])
      .filter((group) => {
        if (!query) return true;

        return [
          group?.nom,
          getDojoName(group),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);
      })
      .slice()
      .sort((a, b) =>
        String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr")
      );
  }, [groups, search]);

  const handleSelectGroup = (group) => {
    setFilters((previous) => ({
      ...previous,
      selectedDojo: group?.dojo_id ? String(group.dojo_id) : previous.selectedDojo,
      filterGroup: group?.nom || "",
    }));
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="min-h-[calc(100dvh-260px)] p-4 sm:p-5 lg:p-6">
        <div className="mx-auto max-w-5xl text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-indigo-50 text-[#013E8E] ring-1 ring-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
            <FaLayerGroup className="text-2xl" />
          </div>

          <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Démarrer l’appel
          </p>

          <h2 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
            Choisir le groupe du cours
          </h2>

          <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
            Sélectionnez rapidement le groupe avant de commencer l’appel.
            L’affichage est optimisé pour une utilisation sur smartphone.
          </p>
        </div>

        <div className="mx-auto mt-6 max-w-5xl space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <FaCalendarAlt />
                Date de l’appel
              </label>

              <input
                type="date"
                value={filters.selectedDate}
                disabled={loading}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    selectedDate: event.target.value,
                  }))
                }
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Saison
              </label>

              <select
                value={filters.selectedSeason}
                disabled={loading}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    selectedSeason: event.target.value,
                  }))
                }
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              >
                <option value="">Toutes</option>
                {seasons.map((season) => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Rechercher
              </label>

              <div className="relative">
                <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Nom du groupe ou dojo..."
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-9 pr-3 text-sm font-bold text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>
          </div>

          {hasSeveralDojos && (
            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/60">
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <FaMapMarkerAlt />
                Filtrer par dojo
              </div>

              <div className="flex gap-2 overflow-x-auto pb-1">
                <button
                  type="button"
                  onClick={() =>
                    setFilters((previous) => ({
                      ...previous,
                      selectedDojo: "",
                      filterGroup: "",
                    }))
                  }
                  className={`shrink-0 rounded-2xl px-4 py-2 text-xs font-bold ring-1 transition ${
                    !filters.selectedDojo
                      ? "bg-[#013E8E] text-white ring-[#013E8E]"
                      : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700"
                  }`}
                >
                  Tous les dojos
                </button>

                {dojos.map((dojo) => (
                  <button
                    key={dojo.id}
                    type="button"
                    onClick={() =>
                      setFilters((previous) => ({
                        ...previous,
                        selectedDojo: String(dojo.id),
                        filterGroup: "",
                      }))
                    }
                    className={`shrink-0 rounded-2xl px-4 py-2 text-xs font-bold ring-1 transition ${
                      String(filters.selectedDojo) === String(dojo.id)
                        ? "bg-[#013E8E] text-white ring-[#013E8E]"
                        : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700"
                    }`}
                  >
                    {dojo.dojo_nom || dojo.nom || `Dojo #${dojo.id}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Groupes disponibles
                </h3>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Touchez un groupe pour lancer l’appel.
                </p>
              </div>

              <span className="inline-flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700">
                <FaUsers />
                {filteredGroups.length}
              </span>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(9)].map((_, index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800"
                  />
                ))}
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                Aucun groupe ne correspond à votre recherche.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {filteredGroups.map((group) => {
                  const dojoName = getDojoName(group);

                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => handleSelectGroup(group)}
                      className={`group flex min-h-[96px] items-center gap-4 rounded-3xl px-4 py-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] ${getGroupColorClass(
                        group
                      )}`}
                    >
                      <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#013E8E] text-white shadow-sm transition group-hover:scale-105">
                        <FaLayerGroup className="text-xl" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-base font-bold">
                          {group.nom || "Groupe"}
                        </span>

                        {dojoName && (
                          <span className="mt-1 flex items-center gap-1 truncate text-xs opacity-70">
                            <FaMapMarkerAlt />
                            {dojoName}
                          </span>
                        )}

                        <span className="mt-2 inline-flex rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-bold text-slate-700 ring-1 ring-black/5 dark:bg-slate-950/70 dark:text-slate-200 dark:ring-white/10">
                          Commencer l’appel
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}